import {
	Box,
	Button,
	Chip,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	MenuItem,
	Select,
	Table,
	TableBody,
	TableCell,
	Tooltip,
	TableHead,
	TableRow,
	TextField,
	Typography,
} from '@mui/material';
import { useCallback, useEffect, useState } from 'react';
import { FaSyncAlt } from 'react-icons/fa';
import constants from '../../constants';
import { httpClient } from '../../providers/myDataProvider';

// GH-676 — organisations (HR / buyers) that registered on partners.grouphug.online/signup.
// Two kinds of rows: self-registered orgs (pending → we approve) and our own HR members that
// logged in with OTP and were auto-provisioned (already approved, linked to their company).

type OrgRow = {
	_id: string;
	org_name: string;
	/** Marketplace wallet (approved orgs only; shared per company for GroupHug HR accounts). */
	budget?: { loaded: number; spent: number; pending: number; balance: number; external: number; shared_accounts?: number };
	contact_name?: string;
	contact_role?: string;
	email: string;
	phone?: string;
	business_id?: string;
	employee_count?: string;
	company?: string | null;
	member?: string | null;
	status: 'pending' | 'approved' | 'rejected';
	status_note?: string;
	last_login_at?: string;
	createdAt: string;
};

const STATUS_LABELS: Record<OrgRow['status'], { label: string; color: 'warning' | 'success' | 'error' }> = {
	pending: { label: 'ממתין לאישור', color: 'warning' },
	approved: { label: 'מאושר · פעיל', color: 'success' },
	rejected: { label: 'נדחה', color: 'error' },
};

export default function OrgBuyersPanel() {
	const [rows, setRows] = useState<OrgRow[]>([]);
	const [loading, setLoading] = useState(true);
	const [statusFilter, setStatusFilter] = useState<string>('');
	const [selected, setSelected] = useState<OrgRow | null>(null);
	const [note, setNote] = useState('');
	const [saving, setSaving] = useState(false);
	// Manual budget load (Uri, 10.9.2026): admins credit an org's marketplace wallet (invoice / bank
	// transfer / goodwill). Admin-only on the server; stamped with the admin's email + note.
	const [loadAmount, setLoadAmount] = useState('');
	const [loadType, setLoadType] = useState<'annual' | 'event'>('event');
	const [loadNote, setLoadNote] = useState('');
	const [loadResult, setLoadResult] = useState('');
	const adminLoad = async (id: string) => {
		const amount = Number(String(loadAmount).replace(/[^\d]/g, ''));
		if (!amount) return;
		setSaving(true);
		setLoadResult('');
		try {
			const res = await httpClient(`${constants.API_URL}partners/admin/org-buyers/${id}/budget`, {
				method: 'POST',
				body: JSON.stringify({ amount, type: loadType, note: loadNote }),
			});
			const b = res.json as { loaded?: number; balance?: number };
			setLoadResult(`נטענו ₪${amount.toLocaleString('he-IL')} · סה"כ נטען ₪${(b.loaded || 0).toLocaleString('he-IL')} · יתרה זמינה ₪${(b.balance || 0).toLocaleString('he-IL')}`);
			setLoadAmount('');
			setLoadNote('');
		} catch (e: any) {
			setLoadResult(`הטעינה נכשלה: ${e?.body?.message?.he || e?.body?.message || e?.message || ''}`);
		} finally {
			setSaving(false);
		}
	};

	const load = useCallback(async () => {
		setLoading(true);
		try {
			const res = await httpClient(
				`${constants.API_URL}partners/admin/org-buyers${statusFilter ? `?status=${statusFilter}` : ''}`,
				{ method: 'GET' },
			);
			setRows((res.json as OrgRow[]) || []);
		} finally {
			setLoading(false);
		}
	}, [statusFilter]);

	useEffect(() => {
		load();
	}, [load]);

	const setStatus = async (id: string, status: OrgRow['status']) => {
		setSaving(true);
		try {
			await httpClient(`${constants.API_URL}partners/admin/org-buyers/${id}/status`, {
				method: 'POST',
				body: JSON.stringify({ status, note }),
			});
			setSelected(null);
			setNote('');
			await load();
		} finally {
			setSaving(false);
		}
	};

	const source = (r: OrgRow) =>
		r.member ? (
			<Chip size="small" variant="outlined" color="info" label="HR של GroupHug" />
		) : (
			<Chip size="small" variant="outlined" label="הרשמה מהאתר" />
		);

	return (
		<Box>
			<Box display="flex" alignItems="center" justifyContent="flex-end" gap={2} mb={2}>
				<Select size="small" value={statusFilter} onChange={(e) => setStatusFilter(String(e.target.value))} displayEmpty sx={{ minWidth: 160 }}>
					<MenuItem value="">כל הסטטוסים</MenuItem>
					<MenuItem value="pending">ממתין לאישור</MenuItem>
					<MenuItem value="approved">מאושר</MenuItem>
					<MenuItem value="rejected">נדחה</MenuItem>
				</Select>
				<Button size="small" startIcon={<FaSyncAlt />} onClick={load} disabled={loading}>
					רענון
				</Button>
			</Box>

			{loading ? (
				<Box textAlign="center" py={6}>
					<CircularProgress />
				</Box>
			) : rows.length === 0 ? (
				<Typography color="text.secondary">אין ארגונים{statusFilter ? ' בסטטוס הזה' : ' עדיין'}.</Typography>
			) : (
				<Table>
					<TableHead>
						<TableRow>
							<TableCell align="right">תאריך</TableCell>
							<TableCell align="right">ארגון</TableCell>
							<TableCell align="right">איש קשר</TableCell>
							<TableCell align="right">מייל</TableCell>
							<TableCell align="right">טלפון</TableCell>
							<TableCell align="right">ח.פ</TableCell>
							<TableCell align="right">גודל</TableCell>
							<TableCell align="right">מקור</TableCell>
							<TableCell align="right">כניסה אחרונה</TableCell>
							<TableCell align="right">תקציב</TableCell>
							<TableCell align="right">סטטוס</TableCell>
							<TableCell align="right"></TableCell>
						</TableRow>
					</TableHead>
					<TableBody>
						{rows.map((r) => {
							const st = STATUS_LABELS[r.status];
							return (
								<TableRow key={r._id} hover>
									<TableCell align="right">{new Date(r.createdAt).toLocaleDateString('he-IL')}</TableCell>
									<TableCell align="right">
										<b>{r.org_name}</b>
									</TableCell>
									<TableCell align="right">
										{r.contact_name || '—'}
										{r.contact_role ? <span style={{ color: '#64748b' }}> · {r.contact_role}</span> : null}
									</TableCell>
									<TableCell align="right">{r.email}</TableCell>
									<TableCell align="right">{r.phone || '—'}</TableCell>
									<TableCell align="right">{r.business_id || '—'}</TableCell>
									<TableCell align="right">{r.employee_count || '—'}</TableCell>
									<TableCell align="right">{source(r)}</TableCell>
									<TableCell align="right">{r.last_login_at ? new Date(r.last_login_at).toLocaleString('he-IL') : '—'}</TableCell>
									<TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
										{r.budget ? (
											<Tooltip
												title={`נטען ₪${r.budget.loaded.toLocaleString('he-IL')} · נוצל ₪${r.budget.spent.toLocaleString('he-IL')} · ממתין ₪${r.budget.pending.toLocaleString('he-IL')}${r.budget.external ? ` · חיצוני ₪${r.budget.external.toLocaleString('he-IL')} (לא נגרע)` : ''}${(r.budget.shared_accounts || 1) > 1 ? ` · ארנק משותף ל-${r.budget.shared_accounts} חשבונות` : ''}`}
											>
												<span>
													<b>₪{r.budget.balance.toLocaleString('he-IL')}</b>
													<span style={{ color: '#64748b' }}> / {r.budget.loaded.toLocaleString('he-IL')}</span>
												</span>
											</Tooltip>
										) : (
											'—'
										)}
									</TableCell>
									<TableCell align="right">
										<Chip size="small" color={st.color} label={st.label} />
									</TableCell>
									<TableCell align="right">
										<Button size="small" onClick={() => { setSelected(r); setNote(r.status_note || ''); setLoadResult(''); }}>
											פרטים
										</Button>
									</TableCell>
								</TableRow>
							);
						})}
					</TableBody>
				</Table>
			)}

			<Dialog open={Boolean(selected)} onClose={() => setSelected(null)} maxWidth="sm" fullWidth dir="rtl">
				{selected && (
					<>
						<DialogTitle>
							{selected.org_name}
							<Chip size="small" color={STATUS_LABELS[selected.status].color} label={STATUS_LABELS[selected.status].label} sx={{ mr: 1 }} />
						</DialogTitle>
						<DialogContent dividers>
							<Typography variant="body2" sx={{ mb: 0.5 }}>
								<b>איש קשר:</b> {selected.contact_name || '—'}{selected.contact_role ? ` (${selected.contact_role})` : ''}
							</Typography>
							<Typography variant="body2" sx={{ mb: 0.5 }}>
								<b>מייל:</b> {selected.email} · <b>טלפון:</b> {selected.phone || '—'}
							</Typography>
							<Typography variant="body2" sx={{ mb: 0.5 }}>
								<b>ח.פ:</b> {selected.business_id || '—'} · <b>גודל:</b> {selected.employee_count || '—'}
							</Typography>
							<Typography variant="body2" sx={{ mb: 2 }}>
								<b>מקור:</b> {selected.member ? 'HR קיים של GroupHug (חשבון נפתח אוטומטית בכניסה)' : 'הרשמה עצמאית מהאתר'}
							</Typography>
							<TextField
								label="הערה (תישמר על הבקשה)"
								fullWidth
								multiline
								minRows={2}
								value={note}
								onChange={(e) => setNote(e.target.value)}
							/>
							{selected.status === 'approved' && (
								<Box sx={{ mt: 3, p: 2, border: '1px solid #e5e7eb', borderRadius: 2 }}>
									<Typography variant="subtitle2" sx={{ mb: 1 }}>
										טעינת תקציב ידנית (מנהלי מערכת)
									</Typography>
									<Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
										מזכה את ארנק המרקטפלייס של הארגון בלי תשלום באתר — למשל תקציב ששולם בחשבונית/העברה. הטעינה נרשמת עם שם המנהל/ת וההערה.
									</Typography>
									<Box display="flex" gap={1} flexWrap="wrap" alignItems="center">
										<TextField
											size="small"
											label="סכום (₪)"
											value={loadAmount}
											onChange={(e) => setLoadAmount(e.target.value.replace(/[^\d]/g, ''))}
											sx={{ width: 140 }}
										/>
										<Button size="small" variant={loadType === 'event' ? 'contained' : 'outlined'} onClick={() => setLoadType('event')}>
											לאירוע
										</Button>
										<Button size="small" variant={loadType === 'annual' ? 'contained' : 'outlined'} onClick={() => setLoadType('annual')}>
											שנתי (5% קאשבק, מ-₪10,000)
										</Button>
									</Box>
									<TextField
										size="small"
										label="הערה (למשל: חשבונית 1234 / העברה בנקאית)"
										fullWidth
										sx={{ mt: 1.5 }}
										value={loadNote}
										onChange={(e) => setLoadNote(e.target.value)}
									/>
									<Box display="flex" alignItems="center" gap={2} sx={{ mt: 1.5 }}>
										<Button variant="contained" disabled={saving || !Number(loadAmount)} onClick={() => adminLoad(selected._id)}>
											טען תקציב
										</Button>
										{loadResult ? <Typography variant="body2">{loadResult}</Typography> : null}
									</Box>
								</Box>
							)}
						</DialogContent>
						<DialogActions sx={{ justifyContent: 'space-between', px: 3, py: 2 }}>
							<Button color="error" disabled={saving || selected.status === 'rejected'} onClick={() => setStatus(selected._id, 'rejected')}>
								דחייה / חסימה
							</Button>
							<Box display="flex" gap={1}>
								{selected.status !== 'pending' && (
									<Button disabled={saving} onClick={() => setStatus(selected._id, 'pending')}>
										החזרה להמתנה
									</Button>
								)}
								<Button variant="contained" color="success" disabled={saving || selected.status === 'approved'} onClick={() => setStatus(selected._id, 'approved')}>
									אישור — פתיחת חשבון
								</Button>
							</Box>
						</DialogActions>
					</>
				)}
			</Dialog>
		</Box>
	);
}
